import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(() => localStorage.getItem('crm_token'));

    const checkAuth = useCallback(async () => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }

        // Handle impersonation token from URL
        const urlParams = new URLSearchParams(window.location.search);
        const impersonateToken = urlParams.get('impersonate_token');
        const impersonateName = urlParams.get('impersonate_name');
        if (impersonateToken) {
            localStorage.setItem('crm_token', impersonateToken);
            localStorage.setItem('crm_impersonating', impersonateName || 'User');
            window.history.replaceState({}, '', window.location.pathname);
            // Fall through to check the token below
        }

        const storedToken = localStorage.getItem('crm_token');
        if (!storedToken) {
            setLoading(false);
            return;
        }

        // Retry logic for transient errors (502/503 during deploys)
        const MAX_RETRIES = 3;
        const RETRY_DELAY = 3000; // 3 seconds between retries

        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            try {
                const res = await axios.get(`${API}/auth/me`, {
                    headers: { Authorization: `Bearer ${storedToken}` },
                    timeout: 10000,
                });
                setUser(res.data);
                setToken(storedToken);
                setLoading(false);
                return; // success — exit
            } catch (err) {
                const status = err?.response?.status;
                // On 401, try refreshing the token before giving up
                if (status === 401) {
                    const refreshToken = localStorage.getItem('crm_refresh_token');
                    if (refreshToken) {
                        try {
                            const refreshRes = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
                            const { access_token: newToken, refresh_token: newRefresh } = refreshRes.data;
                            localStorage.setItem('crm_token', newToken);
                            if (newRefresh) localStorage.setItem('crm_refresh_token', newRefresh);
                            // Retry the /me call with new token
                            const retryRes = await axios.get(`${API}/auth/me`, {
                                headers: { Authorization: `Bearer ${newToken}` },
                                timeout: 10000,
                            });
                            setUser(retryRes.data);
                            setToken(newToken);
                            setLoading(false);
                            return;
                        } catch {
                            // Refresh failed — clear everything
                        }
                    }
                    localStorage.removeItem('crm_token');
                    localStorage.removeItem('crm_refresh_token');
                    localStorage.removeItem('crm_impersonating');
                    setToken(null);
                    setUser(null);
                    setLoading(false);
                    return;
                }
                if (status === 403) {
                    localStorage.removeItem('crm_token');
                    localStorage.removeItem('crm_refresh_token');
                    localStorage.removeItem('crm_impersonating');
                    setToken(null);
                    setUser(null);
                    setLoading(false);
                    return;
                }
                // Transient error (502/503/network) — retry if attempts remain
                if (attempt < MAX_RETRIES) {
                    await new Promise(r => setTimeout(r, RETRY_DELAY));
                } else {
                    // All retries exhausted — keep token, stay "loading" briefly then resolve
                    // so user isn't kicked to login during a deploy
                    setToken(storedToken);
                    setLoading(false);
                }
            }
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const res = await axios.post(`${API}/auth/login`, { email, password });
        const { access_token, refresh_token, user: userData } = res.data;
        localStorage.setItem('crm_token', access_token);
        if (refresh_token) localStorage.setItem('crm_refresh_token', refresh_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const register = async (email, password, name, role = 'admin', org_name = '') => {
        const res = await axios.post(`${API}/auth/register`, { email, password, name, role, org_name });
        const { access_token, refresh_token, user: userData, email_verification_required } = res.data;
        localStorage.setItem('crm_token', access_token);
        if (refresh_token) localStorage.setItem('crm_refresh_token', refresh_token);
        setToken(access_token);
        setUser(userData);
        return { ...userData, email_verification_required };
    };

    const loginWithGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/dashboard';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const setGoogleUser = (userData) => {
        setUser(userData);
    };

    const logout = async () => {
        try {
            const t = token || localStorage.getItem('crm_token');
            await axios.post(`${API}/auth/logout`, {}, {
                headers: t ? { Authorization: `Bearer ${t}` } : {},
                withCredentials: true,
            });
        } catch {}
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_refresh_token');
        localStorage.removeItem('crm_impersonating');
        setToken(null);
        setUser(null);
    };

    const getAuthHeader = useCallback(() => {
        const t = token || localStorage.getItem('crm_token');
        return t ? { Authorization: `Bearer ${t}` } : {};
    }, [token]);

    return (
        <AuthContext.Provider value={{ user, loading, token, login, register, loginWithGoogle, setGoogleUser, logout, getAuthHeader }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};
